import type { TeacherScenario, TeacherScenarioCategory } from "@/config/teacherScenarios";

export type TeacherScenarioKnowledgeProfile = {
  category: TeacherScenarioCategory;
  frameworkIds: string[];
  coachingPrinciples: string[];
  languagePatterns: string[];
  reframeExamples: string[];
};

const CATEGORY_KNOWLEDGE: Record<TeacherScenarioCategory, TeacherScenarioKnowledgeProfile> = {
  parent_family: {
    category: "parent_family",
    frameworkIds: ["nvc", "crucial_conversations", "defensive_patterns"],
    coachingPrinciples: [
      "Lead with curiosity before correction — parents often arrive already feeling judged.",
      "Separate observations from evaluations; describe what you saw, not who the child is.",
      "Name boundaries with warmth — clarity protects the relationship.",
      "When parents escalate, regulate yourself first; they feel your internal state.",
    ],
    languagePatterns: [
      "Instead of 'You're being unreasonable' → 'I want to understand what's most worrying you.'",
      "Instead of defending → 'Help me see what I'm missing from your perspective.'",
      "Instead of 'That's not my fault' → 'Here's what I can commit to from my side.'",
    ],
    reframeExamples: [
      "Hostility may be fear or prior bad experiences with schools — not necessarily about you.",
      "Recording or threatening escalation often signals loss of trust; stay procedural and calm.",
    ],
  },
  student_behavior: {
    category: "student_behavior",
    frameworkIds: ["adolescent_development", "nvc", "connection_before_correction"],
    coachingPrinciples: [
      "Behavior is communication — ask what need or skill gap sits underneath.",
      "Safety first, then connection, then correction.",
      "Students feel who you are more than what you say — regulate before responding.",
      "Strength-based language opens doors; labeling shuts them.",
    ],
    languagePatterns: [
      "Instead of 'Stop being disrespectful' → 'When you rolled your eyes, it landed hard. What's going on?'",
      "Instead of power struggle → 'I'm not taking your phone to win — I'm keeping our agreement so we can learn.'",
      "Instead of shame → 'I'm not giving up on you even when this is hard.'",
    ],
    reframeExamples: [
      "Defiance may be dignity protection or overwhelm, not defiance for its own sake.",
      "Crisis behavior often reflects unmet safety or connection needs.",
    ],
  },
  colleague_staff: {
    category: "colleague_staff",
    frameworkIds: ["crucial_conversations", "professional_repair", "shared_goals"],
    coachingPrinciples: [
      "Anchor to shared purpose — what are you both trying to create for students?",
      "Describe impact without accusation; use 'I' statements about your experience.",
      "Repair beats being right when the working relationship matters.",
      "With supervisors, advocate for context while showing willingness to grow.",
    ],
    languagePatterns: [
      "Instead of 'You undermined me' → 'When that happened in front of students, I felt unsupported.'",
      "Instead of counter-attacking feedback → 'Say more about what you observed — I want to understand.'",
      "Instead of avoiding → 'Can we find 15 minutes to reset how we work together?'",
    ],
    reframeExamples: [
      "Colleague friction often signals misaligned expectations, not bad intent.",
      "Critical feedback may contain useful data even when the delivery stings.",
    ],
  },
  teacher_regulation: {
    category: "teacher_regulation",
    frameworkIds: ["self_compassion", "simplexity", "identity_based_habits"],
    coachingPrinciples: [
      "Acknowledge emotional weight before strategy — teachers need to be seen first.",
      "Simplexity: one small move beats an overwhelming plan.",
      "Separate the story you're telling yourself from what actually happened.",
      "Renewal is built in micro-steps, not heroic overhauls.",
    ],
    languagePatterns: [
      "Instead of 'I failed' → 'That lesson didn't land — what one adjustment might help tomorrow?'",
      "Instead of 'I can't do this' → 'What's one thing still within my control today?'",
      "Instead of pushing through → 'What would enough look like this week?'",
    ],
    reframeExamples: [
      "Burnout numbness is often depletion, not loss of calling.",
      "Bad days are data, not verdicts on your worth as an educator.",
    ],
  },
  school_system: {
    category: "school_system",
    frameworkIds: ["restorative_practice", "compliance_documentation", "team_coordination"],
    coachingPrinciples: [
      "Document before over-promising — especially with IEP/504 and family communication.",
      "Restorative conversations balance accountability with belonging.",
      "Partner with case managers and leadership; you are not alone in systemic issues.",
      "Repair agreements should be specific, doable, and co-created when possible.",
    ],
    languagePatterns: [
      "Instead of 'We'll fix everything' → 'Here's what I can commit to today and who I'm looping in.'",
      "Instead of blame → 'Let's map what happened, who was affected, and what repair looks like.'",
      "Instead of avoidance → 'I want to get this right — can we align on next steps together?'",
    ],
    reframeExamples: [
      "504/IEP gaps are often systemic coordination issues, not individual failure.",
      "Restorative work takes time; rushing to closure undermines trust.",
    ],
  },
};

const DEFAULT_PRINCIPAL_EXPORT_FOCUS: Record<TeacherScenarioCategory, string> = {
  parent_family: "Emphasize proactive communication plan and boundaries shared with leadership.",
  student_behavior: "Highlight safety steps, restorative follow-up, and support needed from admin.",
  colleague_staff: "Focus on professional repair steps and any leadership mediation requested.",
  teacher_regulation: "Frame as wellness plan and sustainable adjustments — not performance deficit.",
  school_system: "Document compliance steps, team coordination, and timeline for follow-through.",
};

export function getKnowledgeProfileForScenario(scenario: TeacherScenario): TeacherScenarioKnowledgeProfile {
  const base = CATEGORY_KNOWLEDGE[scenario.category];
  if (!scenario.relevantFrameworks?.length) return base;
  return { ...base, frameworkIds: scenario.relevantFrameworks };
}

export function getPrincipalExportFocus(scenario: TeacherScenario): string {
  return scenario.principalExportFocus ?? DEFAULT_PRINCIPAL_EXPORT_FOCUS[scenario.category];
}

export function buildKnowledgeExcerpt(profile: TeacherScenarioKnowledgeProfile): string {
  return [
    `Frameworks: ${profile.frameworkIds.join(", ")}`,
    "Coaching principles:",
    ...profile.coachingPrinciples.map((p) => `- ${p}`),
    "Language patterns:",
    ...profile.languagePatterns.map((p) => `- ${p}`),
    "Reframes:",
    ...profile.reframeExamples.map((p) => `- ${p}`),
  ].join("\n");
}

/** JSON-serializable category frameworks for the teacher_scenarios agent brain. */
export function getCategoryFrameworksForAgent(): Record<TeacherScenarioCategory, Omit<TeacherScenarioKnowledgeProfile, "category">> {
  return Object.fromEntries(
    Object.entries(CATEGORY_KNOWLEDGE).map(([key, value]) => [
      key,
      {
        frameworkIds: value.frameworkIds,
        coachingPrinciples: value.coachingPrinciples,
        languagePatterns: value.languagePatterns,
        reframeExamples: value.reframeExamples,
      },
    ]),
  ) as Record<TeacherScenarioCategory, Omit<TeacherScenarioKnowledgeProfile, "category">>;
}
